import {
  confirmationDtoSchema,
  consentOptionsDtoSchema,
  consentRequestAppDtoSchema,
  consentResultDtoSchema,
  type ConfirmationDto,
  type ConsentOptionsDto,
  type ConsentRequestAppDto,
  type ConsentResultDto,
} from '@lib/dto/mcp.dto'
import type { ConsentApproveInput, ConsentCodeInput } from '@lib/input/mcp.input'
import type { Uuid } from '@lib/validators/params'
import { api, publicApi, tenantApi, type ApiClient } from '@/helpers/apiClient'
import type { IMcpConsentService } from '@/types/McpConsentService.types'

function requestPath(requestId: Uuid): string {
  return `/mcp/consent/${encodeURIComponent(requestId)}`
}

function confirmationPath(id: string): string {
  return `/mcp/confirmations/${encodeURIComponent(id)}`
}

/**
 * The connect and confirm pages' API. Bound to the session the person connects with: a
 * member's (`tenantApi`) or staff's (`api`); the app preview needs neither.
 */
export class McpConsentService implements IMcpConsentService {
  constructor(private readonly client: ApiClient) {}

  app(requestId: Uuid): Promise<ConsentRequestAppDto> {
    return this.client.get(`${requestPath(requestId)}/app`, consentRequestAppDtoSchema)
  }

  options(requestId: Uuid): Promise<ConsentOptionsDto> {
    return this.client.get(requestPath(requestId), consentOptionsDtoSchema)
  }

  sendCode(requestId: Uuid, input: ConsentCodeInput): Promise<void> {
    return this.client.postEmpty(`${requestPath(requestId)}/code`, input)
  }

  approve(requestId: Uuid, input: ConsentApproveInput): Promise<ConsentResultDto> {
    return this.client.post(`${requestPath(requestId)}/approve`, input, consentResultDtoSchema)
  }

  deny(requestId: Uuid): Promise<ConsentResultDto> {
    return this.client.post(`${requestPath(requestId)}/deny`, {}, consentResultDtoSchema)
  }

  confirmation(id: string): Promise<ConfirmationDto> {
    return this.client.get(confirmationPath(id), confirmationDtoSchema)
  }

  confirm(id: string): Promise<ConfirmationDto> {
    return this.client.post(`${confirmationPath(id)}/confirm`, {}, confirmationDtoSchema)
  }

  decline(id: string): Promise<ConfirmationDto> {
    return this.client.post(`${confirmationPath(id)}/decline`, {}, confirmationDtoSchema)
  }
}

export const publicConsentService = new McpConsentService(publicApi)
export const memberConsentService = new McpConsentService(tenantApi)
export const staffConsentService = new McpConsentService(api)
